// Feed health — is each source we depend on actually current?
//
// EVERY FEED GETS ITS OWN CLOCK, and that is the whole design. A single
// staleness rule across all of them would be wrong in both directions: the eSMR
// filing is quarterly and runs 45–110 days behind by its nature, so a
// seven-day rule would show it permanently red and teach the reader to ignore
// the colour. The plant's hourly export is supposed to arrive continuously, so
// the same seven-day rule would call thirteen days fine. Age is only meaningful
// against the cadence the source is supposed to keep.
//
// This is the distinction ops/health.ts draws for the fleet console — fetch
// health versus DATA AGE — reduced to the half that matters here. We do not
// fetch these sources; a Python build does, hours earlier. What a client can be
// told is how old what it said is, and whether that is normal for it.
//
// COLOUR IS NEVER THE ONLY SIGNAL. Each row carries a word ("current", "late",
// "stale") beside the light, so the panel survives a projector, a photocopier
// and a colour-blind reader — the same rule the conditions table follows by
// printing every number it colours.

import type { Payload } from "./payload.js";

export type FeedStatus = "ok" | "warn" | "stale" | "absent";

export interface Feed {
  id: string;
  label: string;
  /** What breaks on screen when this one goes — the row's second line. */
  role: string;
  /** The date the source is current to, as it reports it. */
  currentTo: string | null;
  ageDays: number | null;
  /** What "on time" means for this source, in words. */
  cadence: string;
  status: FeedStatus;
  /** Why it is that status, when it is not simply fine. */
  note: string;
}

const WORD: Record<FeedStatus, string> = {
  ok: "current",
  warn: "late",
  stale: "stale",
  absent: "not held",
};

export function statusWord(s: FeedStatus): string {
  return WORD[s];
}

function daysBetween(fromISO: string | null | undefined, todayISO: string): number | null {
  if (fromISO === null || fromISO === undefined || fromISO === "") return null;
  const a = Date.parse(`${fromISO.slice(0, 10)}T00:00:00Z`);
  const b = Date.parse(`${todayISO.slice(0, 10)}T00:00:00Z`);
  if (!Number.isFinite(a) || !Number.isFinite(b)) return null;
  return Math.round((b - a) / 86400000);
}

function grade(age: number | null, warnAfter: number, staleAfter: number): FeedStatus {
  if (age === null) return "absent";
  // A future date is not "very fresh" — it is a clock or timezone fault, and
  // silently grading it green would hide the thing worth knowing.
  if (age < -1) return "warn";
  if (age > staleAfter) return "stale";
  if (age > warnAfter) return "warn";
  return "ok";
}

/**
 * One row per source, graded against its own cadence.
 *
 * `todayISO` is passed rather than read from the clock so this is testable and
 * so the panel grades against the payload's own notion of today — a payload
 * built yesterday should not look fresher because the browser's clock moved.
 */
export function feedHealth(p: Payload, todayISO: string): Feed[] {
  const feeds: Feed[] = [];

  const push = (
    id: string,
    label: string,
    role: string,
    currentTo: string | null,
    cadence: string,
    warnAfter: number,
    staleAfter: number,
    note = "",
  ): void => {
    const ageDays = daysBetween(currentTo, todayISO);
    const status = grade(ageDays, warnAfter, staleAfter);
    feeds.push({ id, label, role, currentTo, ageDays, cadence, status, note });
  };

  // The panel itself. First, because a build that stopped advancing makes every
  // row below it look fine while the whole console is frozen — the failure that
  // most resembles success.
  push("panel", "This panel", "every number on this console",
    p.generated_at?.slice(0, 10) ?? null, "rebuilt daily", 1, 3,
    "if this stops advancing, nothing below it is moving either");

  // Buoy 46215, through the driver window — the payload does not publish station
  // health directly, but the last OBSERVED driver day is exactly that.
  const past = (p.drivers ?? []).filter((d) => d.future !== true);
  const lastObs = past.length > 0 ? (past[past.length - 1]?.date ?? null) : null;
  push("buoy", "Buoy 46215 + stations", "91–95 % of the forecast",
    lastObs, "daily", 2, 5,
    "PSLC1 and 46218 carry the forecast if this one drops");

  push("esmr", "Public intake record (eSMR)", "the measured line on the chart",
    p.last_reported ?? null, "quarterly filing, 45–110 d behind", 120, 200,
    "late by design — this is the gap the console exists to fill");

  const h = p.intake_hourly;
  push("hourly", "Plant hourly export", "the within-day trace, and the 21 % gain at one day",
    h?.last?.slice(0, 10) ?? null, "should be continuous", 2, 5,
    "a file, not a feed — this is what the demo banner is about");

  const f = p.sst_field;
  push("mur", "Sea-surface field (MUR 1 km)", "the site map",
    f?.date ?? null, "daily, ~1 d lag", 3, 7,
    "the map says so rather than drawing calm water");

  push("nrc", "Circulating-water state (NRC)", "the ALARM tier",
    p.decision?.flow_known_to?.slice(0, 10) ?? null, "daily power record", 7, 21,
    "beyond the record the last known state is carried forward");

  return feeds;
}

/** The worst status present, for the rail badge. */
export function worstStatus(feeds: Feed[]): FeedStatus {
  const rank: Record<FeedStatus, number> = { ok: 0, warn: 1, absent: 2, stale: 3 };
  let worst: FeedStatus = "ok";
  for (const f of feeds) if (rank[f.status] > rank[worst]) worst = f.status;
  return worst;
}
